import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import authService from '../services/authService';
import { A, Card, CardTitle, FieldLabel, Input, Pill, Prose, StatusPill } from './account/kit';

/**
 * ChangeEmailSection — Settings › Account › Email (Sept 2026 redesign: one white card).
 *
 * Dual-authorization OTP: a 6-digit code goes to the NEW address (proves the
 * user controls it) AND to the CURRENT address (authorizes the change, guarding
 * a hijacked session). Both must be entered; on the second success the backend
 * swaps the email and revokes all sessions, so we sign out + send to login.
 */
export default function ChangeEmailSection({ pad }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState('idle'); // idle | new | codes | done
  const [newEmail, setNewEmail] = useState('');
  const [newOtp, setNewOtp] = useState('');
  const [curOtp, setCurOtp] = useState('');
  const [newVerified, setNewVerified] = useState(false);
  const [curConfirmed, setCurConfirmed] = useState(false);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [cooldown, setCooldown] = useState(0);

  // Resume a pending change on mount (survives a page reload).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const r = await authService.getEmailChangeStatus();
      if (cancelled || !r.success || !r.data) return;
      setNewEmail(r.data.newEmail);
      setNewVerified(!!r.data.newEmailVerified);
      setCurConfirmed(!!r.data.currentEmailConfirmed);
      setStep('codes');
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const reset = () => {
    setStep('idle'); setNewEmail(''); setNewOtp(''); setCurOtp('');
    setNewVerified(false); setCurConfirmed(false); setError(''); setBusy('');
  };

  const start = useCallback(async (isResend) => {
    setError(''); setBusy('start');
    const r = await authService.startEmailChange(newEmail.trim());
    setBusy('');
    if (!r.success) { setError(r.error || 'Could not start the change.'); return; }
    setNewOtp(''); setCurOtp('');
    if (!isResend) { setNewVerified(false); setCurConfirmed(false); }
    setStep('codes');
    setCooldown(60);
  }, [newEmail]);

  const finish = async () => {
    setStep('done');
    setTimeout(async () => { await logout(); navigate('/login'); }, 1800);
  };

  const verifyNew = async () => {
    setError(''); setBusy('new');
    const r = await authService.verifyNewEmailOtp(newOtp.trim());
    setBusy('');
    if (!r.success) { setError(r.error || 'That code isn’t right.'); return; }
    setNewVerified(true);
    if (r.data?.committed) await finish();
  };

  const confirmCurrent = async () => {
    setError(''); setBusy('current');
    const r = await authService.confirmCurrentEmailOtp(curOtp.trim());
    setBusy('');
    if (!r.success) { setError(r.error || 'That code isn’t right.'); return; }
    setCurConfirmed(true);
    if (r.data?.committed) await finish();
  };

  const cancel = async () => {
    await authService.cancelEmailChange();
    reset();
  };

  const otpInput = (val, set, label) => (
    <Input
      type="text"
      inputMode="numeric"
      maxLength={6}
      value={val}
      onChange={(e) => set(e.target.value.replace(/\D/g, '').slice(0, 6))}
      placeholder="6-DIGIT CODE"
      aria-label={label}
      style={{ width: 200, fontSize: 16, letterSpacing: '.3em' }}
    />
  );

  const codeRow = (label, done, doneText, val, set, onGo, busyKey) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <FieldLabel color={A.grey500}>{label}</FieldLabel>
      {done ? (
        <StatusPill bg={A.verified} style={{ alignSelf: 'flex-start' }}>{doneText}</StatusPill>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          {otpInput(val, set, label)}
          <Pill kind="primary" onClick={onGo} disabled={busy === busyKey || val.length !== 6}>{busy === busyKey ? '…' : 'CONFIRM'}</Pill>
        </div>
      )}
    </div>
  );

  return (
    <Card pad={pad} data-testid="email-card">
      <CardTitle sub="Your login email" right={user?.emailVerified ? <StatusPill bg={A.verified}>VERIFIED</StatusPill> : <StatusPill bg={A.live}>NOT VERIFIED</StatusPill>}>
        EMAIL
      </CardTitle>

      {step === 'idle' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ flex: 1, minWidth: 200, display: 'flex', alignItems: 'center', height: 48, padding: '0 18px', borderRadius: 16, background: A.off, fontSize: 14, boxSizing: 'border-box', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {user?.email}
          </span>
          <Pill kind="secondary" onClick={() => { setError(''); setStep('new'); }}>CHANGE EMAIL</Pill>
        </div>
      )}

      {step === 'new' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <FieldLabel htmlFor="new-email">NEW EMAIL</FieldLabel>
          <Input id="new-email" type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="you@example.com" autoFocus />
          <Prose color={A.grey500}>We’ll send a 6-digit code to this address and to your current one. Both are needed to confirm.</Prose>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Pill kind="primary" onClick={() => start(false)} disabled={busy === 'start' || !newEmail.trim()}>{busy === 'start' ? 'SENDING…' : 'SEND CODES'}</Pill>
            <Pill kind="secondary" onClick={reset} disabled={!!busy}>CANCEL</Pill>
          </div>
        </div>
      )}

      {step === 'codes' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Prose>Enter both codes. One went to <b>{newEmail}</b>, the other to your current address (<b>{user?.email}</b>).</Prose>
          {codeRow('CODE SENT TO NEW EMAIL', newVerified, 'VERIFIED', newOtp, setNewOtp, verifyNew, 'new')}
          {codeRow('CODE SENT TO CURRENT EMAIL', curConfirmed, 'CONFIRMED', curOtp, setCurOtp, confirmCurrent, 'current')}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Pill kind="secondary" onClick={() => start(true)} disabled={cooldown > 0 || !!busy}>{cooldown > 0 ? `RESEND IN ${cooldown}S` : 'RESEND CODES'}</Pill>
            <Pill kind="secondary" onClick={cancel} disabled={!!busy}>CANCEL</Pill>
          </div>
        </div>
      )}

      {step === 'done' && (
        <span style={{ padding: '12px 16px', borderRadius: 16, background: A.ink, color: '#fff', fontSize: 11, fontWeight: 700, letterSpacing: '.12em' }}>
          EMAIL CHANGED. SIGNING YOU OUT — LOG IN AGAIN WITH YOUR NEW EMAIL.
        </span>
      )}

      {error && step !== 'done' && <span role="alert" style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.1em', color: A.danger }}>{error}</span>}
    </Card>
  );
}
